import React, { useState, useEffect } from 'react';
import {
  Box, Grid, Card, Stack, Typography, TextField, Button, Chip, Divider, IconButton, Switch,
  InputAdornment, alpha, useTheme, Snackbar, Alert, Tooltip, CircularProgress, Skeleton, LinearProgress,
  MenuItem,
} from '@mui/material';
import {
  ShieldCheck, ShieldAlert, Copy, Phone, Clock, Gauge, Webhook, Eye, EyeOff, CheckCheck, KeyRound, Receipt, TrendingUp,
} from 'lucide-react';
import {
  ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid, Legend, Tooltip as RTooltip,
} from 'recharts';
import { WA_GREEN, WA_TEAL } from './waData';
import WaSectionHeader, { waGridItem } from './WaLayout';
import { useWaSettings } from './WaSettingsContext';
import { waApi } from './waApi';

// Meta messaging-limit tier → business-initiated conversations allowed per 24h.
const TIER_LIMIT = {
  TIER_50: '50', TIER_250: '250', TIER_1K: '1,000', TIER_10K: '10,000', TIER_100K: '100,000', TIER_UNLIMITED: 'Unlimited',
};
// Numeric cap per tier, for the used/limit progress bar (Unlimited → null).
const TIER_NUM = {
  TIER_50: 50, TIER_250: 250, TIER_1K: 1000, TIER_10K: 10000, TIER_100K: 100000, TIER_UNLIMITED: null,
};
const tierDaily = (t) => (t ? (TIER_LIMIT[t] || t.replace(/^TIER_/, '')) : null);
const QUALITY_COLOR = { GREEN: 'success', YELLOW: 'warning', RED: 'error' };

const titleCase = (s) => (s ? s[0].toUpperCase() + s.slice(1).toLowerCase() : s);
// Indian-rupee formatting, e.g. ₹1,23,456.78.
const fmtINR = (n) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 2 }).format(Number(n) || 0);
const fmtNum = (n) => new Intl.NumberFormat('en-IN').format(Number(n) || 0);
// "YYYY-MM-DD" → "02 Jun" for chart axis/tooltip.
const fmtChartDay = (d) => { try { return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short' }); } catch { return d; } };
// Period-over-period growth → display chip. null = no prior baseline ("New").
const growthMeta = (pct) => {
  if (pct === null || pct === undefined) return { label: 'New', color: 'text.secondary' };
  if (pct > 0) return { label: `▲ ${pct}%`, color: 'success.main' };
  if (pct < 0) return { label: `▼ ${Math.abs(pct)}%`, color: 'error.main' };
  return { label: '0%', color: 'text.secondary' };
};

// Categories shown in the cost reference (Meta bills per delivered message,
// except service/user-initiated which is free).
const COST_ROWS = [
  { key: 'marketing', label: 'Marketing', hint: 'Promotions, offers, broadcasts' },
  { key: 'utility', label: 'Utility', hint: 'Order updates, reminders, post-purchase' },
  { key: 'authentication', label: 'Authentication', hint: 'OTPs, login codes' },
  { key: 'service', label: 'Service', hint: 'Replies in the 24h window — usually free' },
];

// All secret values live in the BACKEND .env (WHATSAPP_*). This screen only
// shows connection status + non-secret display config. Secrets are never sent
// to the browser; masked values here are illustrative placeholders.
const FieldRow = ({ label, children }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1, sm: 2 }} sx={{ py: 1.25 }} alignItems={{ xs: 'flex-start', sm: 'center' }}>
    <Typography sx={{ fontSize: 13, fontWeight: 700, width: { xs: '100%', sm: 200 }, flexShrink: 0, color: 'text.secondary' }}>{label}</Typography>
    <Box sx={{ flex: 1, width: '100%' }}>{children}</Box>
  </Stack>
);

const WhatsAppSettings = () => {
  const theme = useTheme();
  const { readReceipts, setReadReceipts } = useWaSettings();
  // connected/live come from the backend status (derived from whether the
  // WHATSAPP_* env keys are present). Blank .env => "Not connected".
  const [status, setStatus] = useState({ connected: false, live: false, displayPhone: '' });
  const connected = status.connected;
  const [loading, setLoading] = useState(true);
  const [showToken, setShowToken] = useState(false);
  const [display, setDisplay] = useState({
    businessName: 'Reseller Bazzar', about: 'India\'s B2B reseller marketplace. Chat with us for quotes & support.',
    displayPhone: '', countryCode: '+91',
    hoursStart: '09:00', hoursEnd: '19:00',
  });
  const [toast, setToast] = useState('');

  const [usage, setUsage] = useState(null); // live 24h conversation usage from Meta
  const [analytics, setAnalytics] = useState(null); // growth trends from Meta
  const [analyticsLoading, setAnalyticsLoading] = useState(true);
  const [analyticsDays, setAnalyticsDays] = useState(30);
  const [pricing, setPricing] = useState({ currency: 'INR', marketing: 0.78, utility: 0.115, authentication: 0.115, service: 0 });
  const [aiSettings, setAiSettings] = useState({
    enabled: true,
    api_key_set: false,
    model: 'gemini-3.1-flash-lite',
    auto_reply_enabled: true,
    auto_switch_enabled: true,
    ai_only_mode: true,
    human_touch_detection: true,
    inbox_copilot_enabled: true,
    system_prompt: '',
  });
  const set = (k) => (e) => setDisplay((d) => ({ ...d, [k]: e.target.value }));

  // Cloud API number registration (activates sending).
  const [pin, setPin] = useState('');
  const [registering, setRegistering] = useState(false);
  const [registerResult, setRegisterResult] = useState(null); // { severity, msg }

  const handleRegister = async () => {
    if (!pin.trim() || pin.length !== 6 || !/^\d+$/.test(pin.trim())) {
      setRegisterResult({ severity: 'error', msg: 'Enter the 6-digit PIN set up in WhatsApp Manager two-step verification.' });
      return;
    }
    setRegistering(true);
    setRegisterResult(null);
    try {
      const data = await waApi.registerNumber(pin.trim());
      setRegisterResult({ severity: 'success', msg: data.msg || 'Phone number registered successfully.' });
      setPin('');
      waApi.getStatus().then((st) => setStatus(st)).catch(() => {});
    } catch (e) {
      const data = e?.response?.data || {};
      let msg = data.msg || 'Registration failed. Please try again.';
      if (data.code === 133005) msg = 'That PIN is incorrect. Reset two-step verification in WhatsApp Manager, then try again with the new PIN.';
      setRegisterResult({ severity: 'error', msg });
    } finally {
      setRegistering(false);
    }
  };

  // Load saved settings + connection status. Falls back silently if the backend
  // endpoints aren't deployed yet.
  useEffect(() => {
    waApi.getSettings().then(({ settings, status }) => {
      if (status) setStatus(status);
      if (settings) {
        setReadReceipts(settings.read_receipts !== false);
        if (settings.pricing) setPricing((p) => ({ ...p, ...settings.pricing }));
        if (settings.ai) setAiSettings((a) => ({ ...a, ...settings.ai }));
        setDisplay((d) => ({
          ...d,
          businessName: settings.business_profile?.name || d.businessName,
          about: settings.business_profile?.about || d.about,
          displayPhone: status?.displayPhone || d.displayPhone,
          hoursStart: settings.business_hours?.start || d.hoursStart,
          hoursEnd: settings.business_hours?.end || d.hoursEnd,
        }));
      }
    }).catch(() => { /* backend not deployed yet — keep defaults */ })
      .finally(() => setLoading(false));
  }, [setReadReceipts]);

  // Live 24h usage — loaded separately so it never blocks the settings page.
  useEffect(() => {
    waApi.getMessagingUsage()
      .then((d) => setUsage(d))
      .catch(() => setUsage({ available: false }));
  }, []);

  // Growth analytics — reloads when the day-range toggle changes.
  useEffect(() => {
    let alive = true;
    setAnalyticsLoading(true);
    waApi.getAnalytics(analyticsDays)
      .then((d) => { if (alive) setAnalytics(d); })
      .catch(() => { if (alive) setAnalytics({ available: false }); })
      .finally(() => { if (alive) setAnalyticsLoading(false); });
    return () => { alive = false; };
  }, [analyticsDays]);

  const saveSettings = async () => {
    try {
      await waApi.updateSettings({
        read_receipts: readReceipts,
        business_profile: { name: display.businessName, about: display.about },
        business_hours: { start: display.hoursStart, end: display.hoursEnd },
        pricing: {
          currency: pricing.currency,
          marketing: Number(pricing.marketing) || 0,
          utility: Number(pricing.utility) || 0,
          authentication: Number(pricing.authentication) || 0,
          service: Number(pricing.service) || 0,
        },
        ai: aiSettings,
      });
      setToast('Settings saved');
    } catch (e) {
      setToast(e?.response?.data?.msg || 'Could not save settings. Please try again.');
    }
  };

  const webhookUrl = 'https://api.resellerbazzar.in/api/whatsapp/webhook';
  const copy = (text) => {
    // Only claim success when the write actually resolves. On an insecure origin or
    // a denied permission, navigator.clipboard may be missing or reject — show a
    // non-fatal error instead of a misleading "Copied" toast.
    const p = navigator.clipboard?.writeText(text);
    if (p && typeof p.then === 'function') {
      p.then(() => setToast('Copied to clipboard'))
        .catch(() => setToast('Could not copy — copy it manually.'));
    } else {
      setToast('Could not copy — copy it manually.');
    }
  };

  return (
    <Box>
      <WaSectionHeader
        title="Settings"
        subtitle="Connect your WhatsApp Business number, set your business profile, and configure the webhook."
      />

      {loading && (
        <>
          <Skeleton variant="rounded" height={92} sx={{ mb: 2, borderRadius: 1 }} />
          <Skeleton variant="rounded" height={132} sx={{ mb: 2, borderRadius: 1 }} />
          <Grid container spacing={2}>
            {Array.from({ length: 4 }).map((_, i) => (
              <Grid size={{ xs: 12, lg: 6 }} key={i} sx={waGridItem}>
                <Card sx={{ p: 2 }}>
                  <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                    <Skeleton variant="circular" width={20} height={20} />
                    <Skeleton width="45%" height={20} />
                  </Stack>
                  <Skeleton height={16} sx={{ mb: 1 }} />
                  <Skeleton height={16} width="80%" sx={{ mb: 1 }} />
                  <Skeleton height={16} width="60%" />
                </Card>
              </Grid>
            ))}
          </Grid>
        </>
      )}

      {!loading && (
        <>
          {/* Connection status */}
          <Card sx={{
            p: 2, mb: 2, background: connected
              ? `linear-gradient(135deg, ${alpha(WA_GREEN, 0.12)}, ${alpha(WA_TEAL, 0.06)})`
              : alpha(theme.palette.warning.main, 0.08), border: `1px solid ${connected ? alpha(WA_GREEN, 0.4) : alpha(theme.palette.warning.main, 0.4)}`
          }}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }} flexWrap="wrap" gap={1.5}>
              <Box sx={{
                width: { xs: 44, sm: 52 }, height: { xs: 44, sm: 52 }, borderRadius: 2.5, flexShrink: 0, bgcolor: connected ? alpha(WA_GREEN, 0.2) : alpha(theme.palette.warning.main, 0.18),
                display: 'flex', alignItems: 'center', justifyContent: 'center'
              }}>
                {connected ? <ShieldCheck size={26} color={WA_TEAL} /> : <ShieldAlert size={26} color={theme.palette.warning.dark} />}
              </Box>
              <Box sx={{ flex: 1, minWidth: 200 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Typography sx={{ fontSize: 16, fontWeight: 800 }}>{connected ? 'Connected' : 'Not connected'}</Typography>
                  <Chip label={connected ? 'Live' : 'Setup required'} size="small" color={connected ? 'success' : 'warning'} sx={{ fontWeight: 700 }} />
                </Stack>
                <Typography sx={{ fontSize: 13, color: 'text.secondary', mt: 0.25 }}>
                  {connected
                    ? 'Your WhatsApp Business number is connected to the Meta Cloud API.'
                    : 'Add your WhatsApp credentials to the backend environment (WHATSAPP_* in .env) to activate sending and receiving.'}
                </Typography>
              </Box>
            </Stack>
          </Card>

          {/* Growth & analytics — daily trend + period-over-period growth from Meta */}
          <Card sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
              <TrendingUp size={18} color={WA_GREEN} />
              <Typography sx={{ fontSize: 14.5, fontWeight: 800 }}>Growth &amp; analytics</Typography>
              <Chip label="Live from Meta" size="small" variant="outlined" sx={{ fontWeight: 700 }} />
              <Box sx={{ flexGrow: 1 }} />
              <Stack direction="row" spacing={0.5}>
                {[7, 30, 90].map((d) => (
                  <Chip key={d} label={`${d}d`} size="small" onClick={() => setAnalyticsDays(d)}
                    color={analyticsDays === d ? 'primary' : 'default'}
                    variant={analyticsDays === d ? 'filled' : 'outlined'} sx={{ fontWeight: 700 }} />
                ))}
              </Stack>
            </Stack>

            {analyticsLoading ? (
              <Skeleton variant="rounded" width="100%" height={300} />
            ) : analytics?.available ? (
              <>
                <Grid container spacing={1.5} sx={{ mb: 2 }}>
                  {[
                    { label: 'Messages sent', value: fmtNum(analytics.totals.sent), g: analytics.growth.sent },
                    { label: 'Delivered', value: fmtNum(analytics.totals.delivered), g: analytics.growth.delivered, sub: analytics.totals.deliveryRate != null ? `${analytics.totals.deliveryRate}% rate` : null },
                    { label: analytics.estimated?.conversations ? 'Conversations (est.)' : 'Conversations', value: fmtNum(analytics.totals.conversations), g: analytics.growth.conversations },
                    { label: analytics.estimated?.cost ? 'Cost (est.)' : 'Cost', value: fmtINR(analytics.totals.cost), g: analytics.growth.cost },
                  ].map((k) => (
                    <Grid key={k.label} size={{ xs: 6, md: 3 }}>
                      <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: alpha(theme.palette.text.primary, 0.03), height: '100%' }}>
                        <Typography sx={{ fontSize: 11.5, color: 'text.secondary', fontWeight: 700 }}>{k.label}</Typography>
                        <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 800, lineHeight: 1.2 }}>{k.value}</Typography>
                        <Stack direction="row" spacing={0.75} alignItems="baseline" flexWrap="wrap">
                          <Typography sx={{ fontSize: 11.5, fontWeight: 800, color: growthMeta(k.g).color }}>{growthMeta(k.g).label}</Typography>
                          {k.sub && <Typography sx={{ fontSize: 11, color: 'text.disabled' }}>· {k.sub}</Typography>}
                        </Stack>
                      </Box>
                    </Grid>
                  ))}
                </Grid>

                {analytics.series?.length > 0 ? (
                  <Box sx={{ width: '100%', height: 260 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <ComposedChart data={analytics.series} margin={{ top: 8, right: 8, left: -18, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={alpha(theme.palette.text.primary, 0.08)} />
                        <XAxis dataKey="date" tickFormatter={fmtChartDay} tick={{ fontSize: 11 }} minTickGap={28} stroke={theme.palette.text.secondary} />
                        <YAxis tick={{ fontSize: 11 }} allowDecimals={false} stroke={theme.palette.text.secondary} />
                        <RTooltip
                          labelFormatter={fmtChartDay}
                          contentStyle={{ fontSize: 12, borderRadius: 8, border: `1px solid ${alpha(theme.palette.text.primary, 0.1)}` }}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <Area type="monotone" dataKey="sent" name="Sent" stroke={WA_TEAL} fill={alpha(WA_TEAL, 0.15)} strokeWidth={2} />
                        <Area type="monotone" dataKey="delivered" name="Delivered" stroke={WA_GREEN} fill={alpha(WA_GREEN, 0.15)} strokeWidth={2} />
                        <Line type="monotone" dataKey="conversations" name="Conversations" stroke={theme.palette.warning.main} strokeWidth={2} dot={false} />
                      </ComposedChart>
                    </ResponsiveContainer>
                  </Box>
                ) : (
                  <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                    No message activity in the last {analytics.days} days yet — once you start sending, the trend appears here.
                  </Typography>
                )}

                <Typography sx={{ fontSize: 11, color: 'text.disabled', mt: 1 }}>
                  Last {analytics.days} days vs the previous {analytics.days} days.
                  {analytics.tier?.tier ? ` Current tier: ${(TIER_LIMIT[analytics.tier.tier] || analytics.tier.tier)} business-initiated conversations / 24h.` : ''}
                  {analytics.tier?.used24h != null ? ` ${fmtNum(analytics.tier.used24h)} used in the last 24h.` : ''}
                  {(analytics.estimated?.cost || analytics.estimated?.conversations) ? ' Cost is estimated from your message volume and the per-message rate card below (Meta no longer reports per-conversation cost under per-message pricing).' : ''}
                </Typography>
              </>
            ) : (
              <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                {analytics?.error
                  ? `Couldn’t load analytics from Meta: ${analytics.error}`
                  : 'Growth trends appear once the number is connected and the access token has the whatsapp_business_management scope. Otherwise check WhatsApp Manager → Insights.'}
              </Typography>
            )}
          </Card>

          {/* Activate sending — register the number with the Cloud API */}
          <Card sx={{ p: 2, mb: 2 }}>
            <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
              <KeyRound size={18} color={theme.palette.primary.main} />
              <Typography sx={{ fontSize: 14.5, fontWeight: 800 }}>Activate sending (register number)</Typography>
            </Stack>
            <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1.5 }}>
              A number must be registered with the WhatsApp Cloud API before it can send. If sends fail with
              <b> “#133010 Account not registered”</b>, set a 6-digit PIN under <b>WhatsApp Manager → your number →
                Two-step verification</b>, then enter that same PIN here and register.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <TextField
                label="6-digit PIN" size="small" value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 6))}
                inputProps={{ inputMode: 'numeric', maxLength: 6 }}
                sx={{ width: { xs: '100%', sm: 200 } }}
              />
              <Button variant="contained" onClick={handleRegister}
                disabled={pin.length !== 6 || registering}
                startIcon={registering ? <CircularProgress size={14} color="inherit" /> : <KeyRound size={15} />}>
                {registering ? 'Registering…' : 'Register number'}
              </Button>
            </Stack>
            {registerResult && (
              <Alert severity={registerResult.severity} sx={{ mt: 1.5 }} onClose={() => setRegisterResult(null)}>
                {registerResult.msg}
              </Alert>
            )}
          </Card>

          <Grid container spacing={2}>
            {/* Credentials (read-only status — secrets live server-side) */}
            <Grid size={{ xs: 12, lg: 6 }} sx={waGridItem}>
              <Card sx={{ p: 2, height: '100%' }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
                  <ShieldCheck size={18} color={theme.palette.primary.main} />
                  <Typography sx={{ fontSize: 14.5, fontWeight: 800 }}>API Credentials</Typography>
                </Stack>
                <Typography sx={{ fontSize: 12, color: 'text.disabled', mb: 1 }}>
                  Stored securely in the backend <code>.env</code>. Never exposed to the browser.
                </Typography>
                <Divider />
                <FieldRow label="WABA ID">
                  <Chip label={connected ? 'Configured server-side' : 'Not set'} size="small"
                    color={connected ? 'success' : 'default'} variant={connected ? 'filled' : 'outlined'} />
                </FieldRow>
                <Divider />
                <FieldRow label="Phone Number ID">
                  <Chip label={status.displayPhone || (connected ? 'Configured server-side' : 'Not set')} size="small"
                    color={connected ? 'success' : 'default'} variant={connected ? 'filled' : 'outlined'} />
                </FieldRow>
                <Divider />
                <FieldRow label="Access Token">
                  <TextField size="small" fullWidth type={showToken ? 'text' : 'password'} value="" placeholder="Set in backend .env"
                    disabled InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton size="small" onClick={() => setShowToken((s) => !s)}>{showToken ? <EyeOff size={15} /> : <Eye size={15} />}</IconButton>
                        </InputAdornment>)
                    }} />
                </FieldRow>
              </Card>
            </Grid>

            {/* Business profile + hours */}
            <Grid size={{ xs: 12, lg: 6 }} sx={waGridItem}>
              <Card sx={{ p: 2, height: '100%' }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                  <Phone size={18} color={WA_TEAL} />
                  <Typography sx={{ fontSize: 14.5, fontWeight: 800 }}>Business Profile</Typography>
                </Stack>
                <Stack spacing={2}>
                  <TextField label="Business display name" size="small" fullWidth value={display.businessName} onChange={set('businessName')} />
                  <TextField label="About" size="small" fullWidth multiline minRows={2} value={display.about} onChange={set('about')} />
                  {/* Display number is derived from Meta (read-only); country code is a
                      fixed default. Neither is persisted by the backend settings schema
                      (business_profile only stores name + about), so both are disabled
                      to avoid edits that silently revert on reload. */}
                  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                    <TextField label="Display number" size="small" fullWidth placeholder="+91 …" value={display.displayPhone} disabled
                      helperText="Comes from your connected Meta number." />
                    <TextField label="Default country code" size="small" sx={{ width: { xs: '100%', md: 160 } }} value={display.countryCode} disabled />
                  </Stack>
                  <Box>
                    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                      <Clock size={15} color={theme.palette.warning.main} />
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Business hours (away message outside these)</Typography>
                    </Stack>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
                      <TextField label="Start" type="time" size="small" fullWidth value={display.hoursStart} onChange={set('hoursStart')} slotProps={{ inputLabel: { shrink: true } }} InputLabelProps={{ shrink: true }} onClick={(e) => e.target.showPicker?.()} />
                      <TextField label="End" type="time" size="small" fullWidth value={display.hoursEnd} onChange={set('hoursEnd')} slotProps={{ inputLabel: { shrink: true } }} InputLabelProps={{ shrink: true }} onClick={(e) => e.target.showPicker?.()} />
                    </Stack>
                  </Box>
                </Stack>
              </Card>
            </Grid>

            {/* Webhook */}
            <Grid size={{ xs: 12, lg: 6 }} sx={waGridItem}>
              <Card sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                  <Webhook size={18} color={theme.palette.info.main} />
                  <Typography sx={{ fontSize: 14.5, fontWeight: 800 }}>Webhook</Typography>
                </Stack>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1.5 }}>
                  Paste this Callback URL and your Verify Token into Meta App → WhatsApp → Configuration.
                </Typography>
                <TextField size="small" fullWidth value={webhookUrl} InputProps={{
                  readOnly: true,
                  endAdornment: <InputAdornment position="end"><Tooltip title="Copy"><IconButton size="small" onClick={() => copy(webhookUrl)}><Copy size={15} /></IconButton></Tooltip></InputAdornment>
                }} />
                <TextField size="small" fullWidth sx={{ mt: 1.5 }} label="Verify token" placeholder="Matches WHATSAPP_WEBHOOK_VERIFY_TOKEN" disabled />
              </Card>
            </Grid>

            {/* Account health */}
            <Grid size={{ xs: 12, lg: 6 }} sx={waGridItem}>
              <Card sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                  <Gauge size={18} color={theme.palette.success.main} />
                  <Typography sx={{ fontSize: 14.5, fontWeight: 800 }}>Account Health</Typography>
                </Stack>
                <Stack spacing={1.5}>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 0.5, sm: 1 }}>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Quality rating</Typography>
                    <Chip label={status.qualityRating || '—'} size="small"
                      color={QUALITY_COLOR[status.qualityRating] || undefined}
                      variant={QUALITY_COLOR[status.qualityRating] ? 'filled' : 'outlined'} sx={{ fontWeight: 700 }} />
                  </Stack>
                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 0.5, sm: 1 }}>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Messaging tier</Typography>
                    <Typography sx={{ fontSize: 13, fontWeight: 700, color: status.messagingTier ? 'text.primary' : 'text.disabled' }}>
                      {tierDaily(status.messagingTier)
                        ? `${tierDaily(status.messagingTier)} conversations / 24h`
                        : (status.messagingTier || '—')}
                    </Typography>
                  </Stack>

                  {/* Live usage vs the tier limit (last 24h, business-initiated). */}
                  {(() => {
                    const limit = TIER_NUM[status.messagingTier];
                    const used = usage?.available ? usage.businessInitiated : null;
                    if (used == null) {
                      return (
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 0.5, sm: 1 }}>
                          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Used (last 24h)</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: 'text.disabled' }}>
                            {usage ? '—' : 'Loading…'}
                          </Typography>
                        </Stack>
                      );
                    }
                    const pct = limit ? Math.min(100, Math.round((used / limit) * 100)) : 0;
                    const barColor = pct >= 90 ? theme.palette.error.main : pct >= 70 ? theme.palette.warning.main : WA_TEAL;
                    return (
                      <Box>
                        <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 0.5, sm: 1 }} sx={{ mb: 0.5 }}>
                          <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Used (last 24h)</Typography>
                          <Typography sx={{ fontSize: 13, fontWeight: 800, color: WA_TEAL }}>
                            {used.toLocaleString('en-IN')}{limit ? ` / ${limit.toLocaleString('en-IN')}` : ''} conversations
                          </Typography>
                        </Stack>
                        {limit != null && (
                          <LinearProgress variant="determinate" value={pct}
                            sx={{
                              height: 7, borderRadius: 5, bgcolor: theme.palette.background.neutral,
                              '& .MuiLinearProgress-bar': { bgcolor: barColor }
                            }} />
                        )}
                      </Box>
                    );
                  })()}

                  {usage?.available && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 0.5, sm: 1 }}>
                      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>User-initiated (last 24h)</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{usage.userInitiated.toLocaleString('en-IN')} conversations</Typography>
                    </Stack>
                  )}

                  {status.throughputLevel && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 0.5, sm: 1 }}>
                      <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>API throughput</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }}>{titleCase(status.throughputLevel)}</Typography>
                    </Stack>
                  )}

                  <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 0.5, sm: 1 }}>
                    <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>Display name status</Typography>
                    <Chip label={status.nameStatus || '—'} size="small"
                      color={status.nameStatus === 'APPROVED' ? 'success' : undefined}
                      variant={status.nameStatus === 'APPROVED' ? 'filled' : 'outlined'} sx={{ fontWeight: 700 }} />
                  </Stack>

                  {status.verifiedName && (
                    <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" alignItems={{ xs: 'flex-start', sm: 'center' }} spacing={{ xs: 0.5, sm: 1 }}>
                      <Typography sx={{ fontSize: 13, color: 'text.secondary', flexShrink: 0 }}>Verified name</Typography>
                      <Typography sx={{ fontSize: 13, fontWeight: 700, minWidth: 0, maxWidth: '100%' }} noWrap>{status.verifiedName}</Typography>
                    </Stack>
                  )}

                  <Typography sx={{ fontSize: 11.5, color: 'text.disabled', mt: 0.5 }}>
                    {status.messagingTier
                      ? 'The daily limit counts business-initiated conversations (marketing/utility/auth) in a rolling 24-hour window. Service replies inside the 24-hour window don’t count. Usage is live from Meta.'
                      : 'Live quality, tier, usage and name status come from Meta once the number is connected and the token has business-management scope. Otherwise check WhatsApp Manager.'}
                  </Typography>
                </Stack>
              </Card>
            </Grid>

            {/* Privacy & messaging */}
            <Grid size={{ xs: 12, lg: 6 }} sx={waGridItem}>
              <Card sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1.5 }}>
                  <CheckCheck size={18} color="#53bdeb" />
                  <Typography sx={{ fontSize: 14.5, fontWeight: 800 }}>Privacy & Messaging</Typography>
                </Stack>
                <Stack direction="row" alignItems="flex-start" justifyContent="space-between" spacing={1}>
                  <Box sx={{ flex: 1 }}>
                    <Typography sx={{ fontSize: 13.5, fontWeight: 700 }}>Read receipts (blue ticks)</Typography>
                    <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mt: 0.25 }}>
                      When on, the second tick turns blue once a message is read. Turn off to hide read receipts — outgoing
                      ticks stay grey in the inbox.
                    </Typography>
                  </Box>
                  <Switch checked={readReceipts} onChange={(e) => setReadReceipts(e.target.checked)} />
                </Stack>
              </Card>
            </Grid>

            {/* Gemini 1.5 Flash AI Conversational Engine */}
            <Grid size={{ xs: 12 }} sx={waGridItem}>
              <Card sx={{ p: 2.5, border: '1.5px solid rgba(99, 102, 241, 0.3)', bgcolor: alpha('#6366F1', 0.02) }}>
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                  <Stack direction="row" spacing={1.25} alignItems="center">
                    <KeyRound size={20} color="#6366F1" />
                    <Box>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: '#4338CA' }}>
                        Gemini 1.5 Flash AI Engine &amp; Auto-Switching
                      </Typography>
                      <Typography sx={{ fontSize: 12.5, color: 'text.secondary' }}>
                        Human-like AI sales manager ("Jully") converts leads into free seller listings, extracts buyer requirements, and auto-switches to auto-messages when needed.
                      </Typography>
                    </Box>
                  </Stack>
                  <Chip
                    label={aiSettings.enabled ? "AI ACTIVE" : "AI DISABLED"}
                    color={aiSettings.enabled ? "primary" : "default"}
                    size="small"
                    sx={{ fontWeight: 800 }}
                  />
                </Stack>

                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, md: 8 }}>
                    <Card
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        borderColor: aiSettings.api_key_set ? 'success.main' : 'warning.main',
                        bgcolor: aiSettings.api_key_set ? alpha('#10B981', 0.05) : alpha('#F59E0B', 0.05),
                      }}
                    >
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        <Chip
                          label={aiSettings.api_key_set ? 'KEY CONFIGURED IN .ENV' : 'MISSING IN .ENV'}
                          color={aiSettings.api_key_set ? 'success' : 'warning'}
                          size="small"
                          sx={{ fontWeight: 800 }}
                        />
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>
                            {aiSettings.api_key_set ? 'Gemini API Key is Active' : 'Gemini API Key Required'}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: 'text.secondary' }}>
                            {aiSettings.api_key_set
                              ? 'Key is loaded securely via server process.env.GEMINI_API_KEY'
                              : 'Add GEMINI_API_KEY=your_key to server .env file to enable Gemini AI'}
                          </Typography>
                        </Box>
                      </Stack>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, md: 4 }}>
                    <TextField
                      select
                      label="AI Model"
                      size="small"
                      fullWidth
                      value={aiSettings.model || 'gemini-3.1-flash-lite'}
                      onChange={(e) => setAiSettings((a) => ({ ...a, model: e.target.value }))}
                    >
                      <MenuItem value="gemini-3.1-flash-lite">gemini-3.1-flash-lite (Ultra Fast - Recommended)</MenuItem>
                      <MenuItem value="gemini-flash-lite-latest">gemini-flash-lite-latest (Auto Latest Flash Lite)</MenuItem>
                      <MenuItem value="gemini-3.8-flash">gemini-3.8-flash (Latest Standard Flash)</MenuItem>
                      <MenuItem value="gemini-3.5-flash">gemini-3.5-flash (High Reasoning Flash)</MenuItem>
                    </TextField>
                  </Grid>

                  <Grid size={{ xs: 12, md: 3 }}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2, bgcolor: alpha('#10B981', 0.05), borderColor: alpha('#10B981', 0.3) }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#047857' }}>24/7 Pure AI Mode</Typography>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Stops keywords &amp; canned texts</Typography>
                        </Box>
                        <Switch
                          checked={aiSettings.ai_only_mode !== false}
                          onChange={(e) => setAiSettings((a) => ({ ...a, ai_only_mode: e.target.checked }))}
                          color="success"
                        />
                      </Stack>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, md: 3 }}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>AI Auto-Replies</Typography>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Human-like bot responses</Typography>
                        </Box>
                        <Switch
                          checked={aiSettings.auto_reply_enabled !== false}
                          onChange={(e) => setAiSettings((a) => ({ ...a, auto_reply_enabled: e.target.checked }))}
                        />
                      </Stack>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, md: 3 }}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Auto-Switching Engine</Typography>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Auto-Message ↔️ AI</Typography>
                        </Box>
                        <Switch
                          checked={aiSettings.auto_switch_enabled !== false}
                          onChange={(e) => setAiSettings((a) => ({ ...a, auto_switch_enabled: e.target.checked }))}
                        />
                      </Stack>
                    </Card>
                  </Grid>

                  <Grid size={{ xs: 12, md: 3 }}>
                    <Card variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between">
                        <Box>
                          <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Human-Touch Detector</Typography>
                          <Typography sx={{ fontSize: 11, color: 'text.secondary' }}>Auto call-slot trigger</Typography>
                        </Box>
                        <Switch
                          checked={aiSettings.human_touch_detection !== false}
                          onChange={(e) => setAiSettings((a) => ({ ...a, human_touch_detection: e.target.checked }))}
                        />
                      </Stack>
                    </Card>
                  </Grid>
                </Grid>
              </Card>
            </Grid>

            {/* Messaging cost by type */}
            <Grid size={{ xs: 12 }} sx={waGridItem}>
              <Card sx={{ p: 2 }}>
                <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 0.5 }}>
                  <Receipt size={18} color={theme.palette.secondary?.main || theme.palette.primary.main} />
                  <Typography sx={{ fontSize: 14.5, fontWeight: 800 }}>Messaging cost by type</Typography>
                </Stack>
                <Typography sx={{ fontSize: 12.5, color: 'text.secondary', mb: 1.5 }}>
                  Meta bills per delivered message by category. Rates vary by country and change over time — these are your
                  reference values. <b>Verify against Meta’s current rate card before relying on them.</b>
                </Typography>
                <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1.5 }}>
                  <TextField label="Currency" size="small" sx={{ width: 120 }} value={pricing.currency}
                    onChange={(e) => setPricing((p) => ({ ...p, currency: e.target.value.toUpperCase().slice(0, 4) }))} />
                  <Typography sx={{ fontSize: 12, color: 'text.disabled' }}>Cost per delivered message</Typography>
                </Stack>
                <Grid container spacing={1.5}>
                  {COST_ROWS.map((row) => (
                    <Grid size={{ xs: 12, sm: 6, md: 3 }} key={row.key} sx={waGridItem}>
                      <Box sx={{ p: 1.5, borderRadius: 2, border: `1px solid ${theme.palette.divider}`, bgcolor: theme.palette.background.neutral, height: '100%' }}>
                        <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{row.label}</Typography>
                        <Typography sx={{ fontSize: 11, color: 'text.disabled', mb: 1, minHeight: 28 }}>{row.hint}</Typography>
                        <TextField size="small" fullWidth type="number" value={pricing[row.key]}
                          onChange={(e) => setPricing((p) => ({ ...p, [row.key]: e.target.value }))}
                          InputProps={{ startAdornment: <InputAdornment position="start">{pricing.currency}</InputAdornment> }}
                          inputProps={{ min: 0, step: 0.001, 'aria-label': `${row.label} cost per message` }} />
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              </Card>
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent={{ xs: 'stretch', md: 'flex-end' }} sx={{ mt: 2 }}>
            <Button variant="contained" onClick={saveSettings} sx={{ width: { xs: '100%', sm: 'auto' } }}>Save Settings</Button>
          </Stack>
        </>
      )}

      <Snackbar open={!!toast} autoHideDuration={2200} onClose={() => setToast('')} anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}>
        <Alert severity={/could not|fail|error|invalid|unable/i.test(toast) ? 'error' : 'success'} variant="filled" onClose={() => setToast('')}>{toast}</Alert>
      </Snackbar>
    </Box>
  );
};

export default WhatsAppSettings;
